// Detailed explanation of why a QR purchase is a draft: per reason code a friendly label,
// the detail line the outpost wrote into `reviewNote`, and a "What to do" hint.
import {reviewReasonLabel} from "./qrReasonLabels";

export const QR_REVIEW_REASON_HINTS = {
    NO_EMPTY_WEIGHT: "Set the vehicle's empty weight on the Vehicles page (or weigh it empty), then complete this purchase.",
    NOT_ON_SCALE: "Check the photo. If the truck was on the scale, capture the weight and save; otherwise discard.",
    WEIGHBRIDGE_ERROR: "Check the weighbridge is switched on and its cable is connected, then capture the weights and save, or discard.",
    WEIGHT_NOT_STABLE: "Check the weight is correct before completing.",
    CAMERA_FAILED: "Check the camera; you can still complete the purchase.",
    NET_WEIGHT_INVALID: "Check the loaded and empty weights (and the vehicle's empty weight), correct them, then complete.",
    SUPPLIER_NOT_FOUND: "Pull the latest data from the server, or pick the supplier on the purchase, then complete.",
    ITEM_NOT_FOUND: "Pull the latest data from the server, or pick the product on the purchase, then complete.",
    MULTIPLE_PENDING: "This truck has more than one unfinished purchase. Check them and discard the extra ones.",
    WEIGHBRIDGE_MISMATCH: "Check the truck was weighed on the right weighbridge before completing.",
    WEIGHT_NOT_DROPPED: "Check whether the truck unloaded; complete or discard.",
    PROCESSING_ERROR: "Check the details, capture the weights if needed, then complete or discard.",
};

export function reviewReasonHint(code) {
    return QR_REVIEW_REASON_HINTS[code] || "Check the details, then complete or discard.";
}

// Splits reviewNote into lines. The outpost writes "[CODE] Sentence." per reason (QrReviewNotes.LINE);
// "CODE: sentence" is also accepted. Lines are matched to reasons BY CODE, never by position: lines can
// be appended later, a code can appear on several lines, and a reason may have no line (older drafts).
// Lines without a prefix (e.g. typed by hand) are kept as-is. The bracket prefix is never shown.
function parseNote(note) {
    const byCode = {};
    const plain = [];
    String(note || "").split("\n").map(l => l.trim()).filter(Boolean).forEach(line => {
        // "CODE: " only counts for a known reason code, so a hand-typed "NOTE: ..." line is still shown
        const colon = line.match(/^([A-Z][A-Z0-9_]+)\s*:\s*(.*)$/);
        const m = line.match(/^\[([A-Z0-9_]+)\]\s*(.*)$/) || (colon && colon[1] in QR_REVIEW_REASON_HINTS ? colon : null);
        if (m) {
            if (m[2]) (byCode[m[1]] = byCode[m[1]] || []).push(m[2]);
        } else {
            plain.push(line);
        }
    });
    return {byCode, plain};
}

/**
 * [{code, label, detail, hint}] for a purchase's reviewReasons + reviewNote.
 * `detail` is the reason's note line(s) joined with "\n" (render with white-space: pre-line), or null
 * when the reason has no line - show the label only. Unprefixed note lines follow as {code: null,
 * label: "Note", hint: null}.
 */
export function buildReviewDetails(reasons, note) {
    const codes = Array.from(new Set(Array.isArray(reasons) ? reasons : []));
    const {byCode, plain} = parseNote(note);
    const details = codes.map(code => ({
        code,
        label: reviewReasonLabel(code),
        detail: byCode[code] ? byCode[code].join("\n") : null,
        hint: reviewReasonHint(code),
    }));
    plain.forEach(line => details.push({code: null, label: "Note", detail: line, hint: null}));
    return details;
}

// Detail line for one reason (used as a hover title in the scan log).
export function reviewDetailsTitle(reasons, note) {
    return buildReviewDetails(reasons, note)
        .map(d => (d.detail ? `${d.label}: ${d.detail}` : d.label))
        .join("\n");
}
